/**
 * Patient — field-level effects.
 *
 * Migrated from: patient/patient-base.coffee
 *
 * Old validators → new effects:
 *   StatusValidate           → StatusValidate
 *   RestrictRCC              → RestrictRCC
 *   HideMaxAccumulator       → HideMaxAccumulator
 *   SetRole                  → SetRole
 *   PadMemberID              → PadMemberID
 *   SetCompanyId             → SetCompanyId
 *   validateOnHoldStatus     → validateOnHoldStatus
 *   SetPatientTags           → SetPatientTags
 *   StatusChangeDate         → StatusChangeDate
 *   GroupStatusValidate      → GroupStatusValidate
 *   SetOverridePBMAndMedical → SetOverridePBMAndMedical
 *   ResendWelcomePacket      → ResendWelcomePacket
 *
 * Skipped:
 *   DuplicatePatient — already migrated as Tier 3 form validator in formValidators/patient.ts
 *   handle(dfd) init logic — default values belong in backend definitions or form onInit
 */
import dayjs from "dayjs";

import { buildApiUrl, fetchAPI, getSessionRoleSlug } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

// ─── StatusValidate ─────────────────────────────────────────────────────────
// Old: DSLFx.Validators.StatusValidate
// Trigger field: status_restricted
// When status is set to "On-Service", verifies the patient has at least one
// approved PAP, CAP, or other funding record. Resets status if none found.
export const StatusValidate: FieldHandler = (field, { form, editID }) => {
	void (async () => {
		const status = field.value;

		const nextYear = dayjs().year() + 1;
		form.setFieldState("max_accumulator_reset_date", (s) => {
			s.value = dayjs(`${nextYear}-01-01`);
		});

		if (!status || status.length === 0) return;
		if (status !== "On-Service") {
			form.setFieldState("status", (s) => {
				s.value = status;
			});
			return;
		}

		const patientId = editID;
		if (!patientId) return;

		try {
			const papData = await fetchAPI(
				buildApiUrl(
					`/patient_pap/list?limit=1&_status=eq:Approved&_patient_id=eq:${patientId}`
				)
			);
			const papRows = Array.isArray(papData)
				? papData
				: (papData?.data ?? []);
			if (papRows.length > 0) {
				form.setFieldState("status", (s) => {
					s.value = status;
				});
				return;
			}

			const capData = await fetchAPI(
				buildApiUrl(
					`/patient_copay_program/list?limit=1&_status=eq:Approved&_patient_id=eq:${patientId}`
				)
			);
			const capRows = Array.isArray(capData)
				? capData
				: (capData?.data ?? []);
			if (capRows.length > 0) {
				form.setFieldState("status", (s) => {
					s.value = status;
				});
				return;
			}

			const fundingData = await fetchAPI(
				buildApiUrl(
					`/patient_other_funding/list?limit=1&_status=eq:Approved&_patient_id=eq:${patientId}`
				)
			);
			const fundingRows = Array.isArray(fundingData)
				? fundingData
				: (fundingData?.data ?? []);
			if (fundingRows.length > 0) {
				form.setFieldState("status", (s) => {
					s.value = status;
				});
				return;
			}

			form.setFieldState("status_restricted", (s) => {
				s.value = "";
			});
			field.setFeedback({
				type: "error",
				messages: [
					"Cannot find any active PAPs, CAPs, or other funding for the patient. Cannot be On-Service.",
				],
			});
		} catch (err) {
			console.error("StatusValidate: API error", err);
			form.setFieldState("status_restricted", (s) => {
				s.value = "";
			});
		}
	})();
	return "";
};

// ─── RestrictRCC ────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.RestrictRCC
// Trigger field: rcc_id or plan_id
// Verifies the assigned RCC user is not restricted for the selected plan.
export const RestrictRCC: FieldHandler = (field, { form }) => {
	void (async () => {
		const rccId = form.getFieldState("rcc_id")?.value?.id;
		const planId = form.getFieldState("plan_id")?.value?.id;
		if (!rccId || !planId) return;

		try {
			const data = await fetchAPI(
				buildApiUrl(`/plan/${parseInt(planId)}`)
			);
			const plan = Array.isArray(data) ? data[0] : data;
			if (!plan?.restricted_rcc) return;

			const isRestricted = plan.restricted_rcc.some(
				(el: any) => parseInt(el) === parseInt(rccId)
			);
			if (isRestricted) {
				field.setFeedback({
					type: "error",
					messages: [
						"Assigned RCC user is restricted for selected plan!",
					],
				});
			} else {
				field.setFeedback({ type: "error", messages: [] });
			}
		} catch (err) {
			console.error("RestrictRCC: API error", err);
		}
	})();
	return "";
};

// ─── HideMaxAccumulator ─────────────────────────────────────────────────────
// Old: DSLFx.Validators.HideMaxAccumulator
// Trigger field: plan_group_id / plan_id / sec_plan_group_id / do_not_bill
// Toggles visibility of max accumulator, do_not_bill, and reason_for_not_billed
// fields based on specific plan/group combinations.
export const HideMaxAccumulator: FieldHandler = (field, { form }) => {
	const primaryGroupId = form.getFieldState("plan_group_id")?.value?.id;
	const planId = form.getFieldState("plan_id")?.value?.id;
	const doNotBill = form.getFieldState("do_not_bill")?.value;

	const specialGroupIds = [938, 932, 1316];
	const numericPrimaryGroupId = parseInt(primaryGroupId);

	form.setFieldState("reason_for_not_billed", (s) => {
		s.visible = false;
	});

	if (
		parseInt(planId) === 141 &&
		specialGroupIds.includes(numericPrimaryGroupId)
	) {
		form.setFieldState("max_accumulator", (s) => {
			s.visible = false;
		});
		form.setFieldState("max_ac_for_specific_plan", (s) => {
			s.visible = true;
		});
		form.setFieldState("do_not_bill", (s) => {
			s.visible = true;
		});
		if (doNotBill === "Yes") {
			form.setFieldState("reason_for_not_billed", (s) => {
				s.visible = true;
			});
		}
	} else {
		form.setFieldState("max_accumulator", (s) => {
			s.visible = true;
		});
		form.setFieldState("max_ac_for_specific_plan", (s) => {
			s.visible = false;
		});
		form.setFieldState("do_not_bill", (s) => {
			s.visible = false;
		});
	}

	return "";
};

// ─── SetRole ────────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.SetRole
// Trigger field: user_role
// Resets user_role to the current logged-in user's role (prevents manual changes).
export const SetRole: FieldHandler = (field, { form }) => {
	const currentUserRole = getSessionRoleSlug();
	if (!currentUserRole) return "";
	if (field.value === currentUserRole) return "";

	form.setFieldState("user_role", (s) => {
		s.value = currentUserRole;
	});
	return "";
};

// ─── PadMemberID ────────────────────────────────────────────────────────────
// Old: DSLFx.Validators.PadMemberID
// Trigger field: external_id or plan_id
// Zero-pads external_id to 9 characters when plan_id is "36".
export const PadMemberID: FieldHandler = (field, { form }) => {
	const externalId = form.getFieldState("external_id")?.value;
	const planId = form.getFieldState("plan_id")?.value?.id;

	if (!externalId || !planId) return "";

	const padded = String(externalId).padStart(9, "0");
	if (padded !== externalId) {
		form.setFieldState("external_id", (s) => {
			s.value = padded;
		});
	}
	return "";
};

// ─── SetCompanyId ───────────────────────────────────────────────────────────
// Old: DSLFx.Validators.SetCompanyId
// Trigger field: plan_id
// Syncs company_id with plan_id value.
export const SetCompanyId: FieldHandler = (field, { form }) => {
	const planId = form.getFieldState("plan_id")?.value?.id;
	const companyId = form.getFieldState("company_id")?.value;

	if (!planId) return "";
	if (planId === companyId) return "";

	form.setFieldState("company_id", (s) => {
		s.value = planId;
	});
	return "";
};

// ─── validateOnHoldStatus ───────────────────────────────────────────────────
// Old: DSLFx.Validators.validateOnHoldStatus
// Trigger field: onhold_reason (or the field key it's wired to)
// Shows and requires onhold_reason_other when reason is "Other" and status is "On-Hold".
export const ValidateOnHoldStatus: FieldHandler = (field, { form }) => {
	const { value } = field;
	const status = form.getFieldState("status")?.value;

	if (value === "Other" && status === "On-Hold") {
		form.setFieldState("onhold_reason_other", (s) => {
			s.visible = true;
			s.required = true;
		});
	} else {
		form.setFieldState("onhold_reason_other", (s) => {
			s.visible = false;
			s.required = false;
			s.value = "";
		});
	}
	return "";
};

// ─── SetPatientTags ─────────────────────────────────────────────────────────
// Old: DSLFx.Validators.SetPatientTags
// Trigger field: group_status (or whatever field key it's wired to)
// Looks up the tag by the group_status name and sets patient_tags.
export const SetPatientTags: FieldHandler = (field, { form }) => {
	void (async () => {
		const fieldVal = field.value;
		if (!fieldVal || fieldVal.length === 0) return;

		try {
			const data = await fetchAPI(
				buildApiUrl(
					`/tag/list?_name=eq:${encodeURIComponent(fieldVal)}`
				)
			);
			const rows = Array.isArray(data) ? data : (data?.data ?? []);
			if (rows.length > 0) {
				form.setFieldState("patient_tags", (s) => {
					s.value = [{ ...rows[0] }];
				});
			} else {
				form.setFieldState(field.path.toString(), (s) => {
					s.value = "";
				});
				form.setFieldState("patient_tags", (s) => {
					s.value = [];
				});
				field.setFeedback({
					type: "warning",
					messages: [
						"Cannot find corresponding patient tag. Please add selected group status in Manage > Patient Tags and then try again.",
					],
				});
			}
		} catch (err) {
			console.error("SetPatientTags: API error", err);
			form.setFieldState(field.path.toString(), (s) => {
				s.value = "";
			});
			form.setFieldState("patient_tags", (s) => {
				s.value = [];
			});
			field.setFeedback({
				type: "warning",
				messages: [
					"Cannot find corresponding patient tag. Please add selected group status in Manage > Patient Tags and then try again.",
				],
			});
		}
	})();
	return "";
};

// ─── StatusChangeDate ───────────────────────────────────────────────────────
// Old: DSLFx.Validators.StatusChangeDate
// Trigger field: status
// Updates status_chng_date to today when status changes from its original value,
// or preserves the original date if status hasn't changed.
export const StatusChangeDate: FieldHandler = (field, { form, formMode }) => {
	const today = dayjs().format("YYYY-MM-DD");

	if (formMode === "create") {
		form.setFieldState("status_chng_date", (s) => {
			s.value = today;
		});
		return "";
	}

	// In edit mode, we always set to today since we can't compare with preset.
	// The original date is preserved if the value hasn't actually changed
	// (the handler only fires on value change events).
	form.setFieldState("status_chng_date", (s) => {
		s.value = today;
	});
	return "";
};

// ─── GroupStatusValidate ────────────────────────────────────────────────────
// Old: DSLFx.Validators.GroupStatusValidate
// Trigger field: status
// Prevents Pending/On-Service status with Off-Service group status.
// Also checks for unconfirmed dispenses when changing to Inactive.
export const GroupStatusValidate: FieldHandler = (field, { form, editID }) => {
	void (async () => {
		const status = form.getFieldState("status")?.value;
		const groupStatus = form.getFieldState("group_status")?.value;

		if (
			["Pending", "On-Service"].includes(status) &&
			groupStatus === "Off-Service"
		) {
			field.setFeedback({
				type: "error",
				messages: [
					'A member that is Active can never have a group status of "Off-Service"',
				],
			});
			return;
		}

		field.setFeedback({ type: "error", messages: [] });

		if (!editID) return;
		if (status !== "Inactive") return;

		try {
			const data = await fetchAPI(
				buildApiUrl(
					`/patient_order_dispense/list?limit=1&_patient_id=eq:${editID}&_status=eq:Unconfirmed`
				)
			);
			const rows = Array.isArray(data) ? data : (data?.data ?? []);
			if (rows.length > 0) {
				field.setFeedback({
					type: "error",
					messages: [
						"This patient has Unconfirmed dispenses. Cannot set Inactive status.",
					],
				});
			} else {
				field.setFeedback({ type: "error", messages: [] });
			}
		} catch (err) {
			console.error("GroupStatusValidate: API error", err);
		}
	})();
	return "";
};

// ─── ResendWelcomePacket ────────────────────────────────────────────────────
// Old: DSLFx.Validators.ResendWelcomePacket
// Trigger field: email
// Checks if a patient-role user exists for this email and whether they've logged in.
export const ResendWelcomePacket: FieldHandler = (field, { form }) => {
	void (async () => {
		const email = form.getFieldState("email")?.value;
		if (!email) return;

		try {
			const data = await fetchAPI(
				buildApiUrl(
					`/user/list?_role=eq:patient&_email=eq:${encodeURIComponent(email)}`
				)
			);
			const rows = Array.isArray(data) ? data : (data?.data ?? []);
			if (rows.length > 0) {
				const [user] = rows;
				form.setFieldState("user_login", (s) => {
					s.value = !user?.last_login ? "No" : "Yes";
				});
			}
		} catch (err) {
			console.error("ResendWelcomePacket: API error", err);
		}
	})();
	return "";
};
